import { useEffect, useMemo, useRef } from 'react';

import _ from 'lodash';

import { checkIsPurchaseFlow } from 'utils/loanApplication';

import {
  GOVERNMENT_REFINANCE_PURPOSE_FIELD_NAME,
  IS_FIELD_DEFAULT_A_USER_EDIT,
} from '../LoanEditorCustomFormDrawer.constants';
import {
  buildDownPaymentSyncUpdates,
  checkIsDownPaymentPlaced,
  getGovernmentRefinancePurposeDefaultOption,
  pickDownPaymentSyncValues,
} from '../LoanEditorCustomFormDrawer.loanInfoDefaults.utils';

const useLoanInfoFieldDefaults = ({
  applicationData,
  formState,
  initialFormValues,
  placedFieldPaths,
  purchasePrice,
  setInForm,
  setMultipleInForm,
}) => {
  const previousDownPaymentValuesRef = useRef(null);

  const loanPurpose = formState?.loanPurpose?.value ?? applicationData.loanPurpose;
  const governmentRefinancePurposeDefaultOption = getGovernmentRefinancePurposeDefaultOption({
    placedFieldPaths,
    mortgageType: applicationData.mortgageType,
    loanPurpose,
    governmentRefinancePurpose: _.isUndefined(formState?.governmentRefinancePurpose)
      ? applicationData.governmentRefinancePurpose
      : formState.governmentRefinancePurpose?.value,
  });
  const isDownPaymentSyncEnabled =
    checkIsPurchaseFlow(loanPurpose) &&
    checkIsDownPaymentPlaced(placedFieldPaths) &&
    !_.isNil(formState?.baseLoanAmount);
  const { downPaymentValue, downPaymentPercent, baseLoanAmount } =
    pickDownPaymentSyncValues(formState);
  const seededDownPaymentValues = useMemo(
    () => pickDownPaymentSyncValues(initialFormValues),
    [initialFormValues],
  );

  useEffect(() => {
    if (_.isNil(governmentRefinancePurposeDefaultOption)) {
      return;
    }

    setInForm(
      GOVERNMENT_REFINANCE_PURPOSE_FIELD_NAME,
      governmentRefinancePurposeDefaultOption,
      IS_FIELD_DEFAULT_A_USER_EDIT,
    );
  }, [governmentRefinancePurposeDefaultOption, setInForm]);

  useEffect(() => {
    previousDownPaymentValuesRef.current = seededDownPaymentValues;
  }, [seededDownPaymentValues]);

  useEffect(() => {
    const currentValues = { downPaymentValue, downPaymentPercent, baseLoanAmount };
    const previousValues = previousDownPaymentValuesRef.current;

    if (!isDownPaymentSyncEnabled || _.isNil(previousValues)) {
      return;
    }

    if (_.isEqual(currentValues, seededDownPaymentValues)) {
      previousDownPaymentValuesRef.current = currentValues;

      return;
    }

    const updates = buildDownPaymentSyncUpdates({ previousValues, currentValues, purchasePrice });

    previousDownPaymentValuesRef.current = { ...currentValues, ...updates };

    if (!_.isEmpty(updates)) {
      setMultipleInForm(updates);
    }
  }, [
    baseLoanAmount,
    downPaymentPercent,
    downPaymentValue,
    isDownPaymentSyncEnabled,
    purchasePrice,
    seededDownPaymentValues,
    setMultipleInForm,
  ]);
};

export default useLoanInfoFieldDefaults;
